import { Button } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { AlertCircleIcon } from 'src/components/icons';
import { APP_SETUP_PAGE, ModuleType } from 'src/constants';
import { RouteContext } from 'src/context';
import history from 'src/history';
import { useAppDispatch } from 'src/hooks/useStore';
import { AlertModal } from 'src/legacy/components/Modals';
import { useEditingApp } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/useEditingApp';
import { typography15MediumStyle } from 'src/legacy/components/Text';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { DeleteAppBuilderModal } from 'src/routes/appSetup/builder/components/DeleteAppBuilderModal';
import { useListAppBuildersQuery } from 'src/services/api/appBuilderApi';
import {
  useDeleteAppMutation,
  useGetInstallsQuery,
} from 'src/services/api/applicationsApi';
import {
  updateExtensionSettingsAction,
  updateModuleSettings,
} from 'src/store/settings/actions';
import { GraySmall, NonHoverBorder, red, redDark } from 'src/theme/colors';

const useStyles = makeStyles()((theme) => ({
  deleteButton: {
    marginTop: theme.spacing(3),
    backgroundColor: red,
    borderColor: red,
    '&:hover': {
      backgroundColor: red,
    },
  },
  disabledButton: {
    marginTop: theme.spacing(3),
    backgroundColor: 'transparent',
    borderColor: NonHoverBorder,
    color: red,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  embedsContent: {
    color: redDark,
    ...typography15MediumStyle,
  },
  actionModal: {
    '& .MuiPaper-root': {
      width: 544,
    },
  },
  removeAppContent: {
    color: GraySmall,
    paddingTop: theme.spacing(0.5),
  },
}));

type AlertModalDataType = {
  modalBody: React.ReactElement;
  modalSuccessActionLabel: string;
  modalTitle: string;
  moduleType?: ModuleType;
  moduleId?: string;
};

interface removeAppQuery {
  moduleId?: string;
  appId?: string;
}

/**
 * @description - Responsible for rendering the remove app button and the modal.
 * For core apps (Forms, Helpdesk, Billing etc), we only disable the app.
 * For other apps, we delete the app.
 * Additionally, we do not render this component when we're in add mode.
 */
export const RemoveApp = () => {
  const { query } = React.useContext(RouteContext);
  const appModule = useEditingApp();
  const { t } = useTranslation();
  const [deletingModuleData, setDeletingModuleData] =
    React.useState<AlertModalDataType | null>(null);
  const [isDeletingModule, setIsDeletingModule] = React.useState(false);
  const isCoreApp = appModule?.type === 'module';
  const isAddMode = 'preset' in query; // Represents if we are in add mode or edit mode
  const { data: { moduleSettings = [], extensionsSettings } = {} } =
    useGetInstallsQuery('withFolders');
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const moduleConfig =
    'moduleId' in query &&
    moduleSettings.find(
      (module) => module.id === (query as removeAppQuery).moduleId,
    );
  const isMarketplaceApp = !!(
    moduleConfig && extensionsSettings?.[moduleConfig.id]?.appId
  );
  const [deleteCustomApp] = useDeleteAppMutation();
  const { data: appBuilders } = useListAppBuildersQuery();
  const appId: string =
    'appId' in query ? ((query as removeAppQuery).appId as string) : '';
  const builder = appModule
    ? appBuilders?.find((b) => b.installedModuleId === appModule.id)
    : undefined;
  const [showBuilderDeleteModal, setShowBuilderDeleteModal] =
    React.useState(false);
  const handleDeleteModuleActionClicked = () => {
    if (!appModule) return;

    if (builder) {
      setShowBuilderDeleteModal(true);
      return;
    }

    if (appModule.type === 'module') {
      setDeletingModuleData({
        moduleType: appModule.type,
        moduleId: appModule.id,
        modalTitle: t(($) => $.page_apps.remove_app_modal.disable.confirm),
        modalBody: (
          <BaseTypography fontType="15Regular">
            {t(($) => $.page_apps.remove_app_modal.disable.description)}
          </BaseTypography>
        ),
        modalSuccessActionLabel: t(($) => $.common.disable),
      });
    } else {
      const extensionModuleEmbedCount =
        extensionsSettings?.[appModule.id]?.embedCount ?? 0;

      // when extension has embeds, show the embed count text,
      // otherwise do not.
      const stagedModalBody =
        extensionModuleEmbedCount > 0 ? (
          <div>
            <BaseTypography fontType="15Regular">
              The connection data along with &nbsp;
              <span className={classes.embedsContent}>
                {extensionModuleEmbedCount}&nbsp;
                {extensionModuleEmbedCount > 1 ? 'embeds' : 'embed'}
              </span>
              &nbsp; will be deleted.
            </BaseTypography>
          </div>
        ) : (
          <BaseTypography>The connection data will be deleted.</BaseTypography>
        );
      setDeletingModuleData({
        moduleType:
          appModule.type === 'authenticated' ? 'global' : appModule.type,
        moduleId: appModule.id,
        modalTitle: t(($) => $.page_apps.remove_app_modal.remove.confirm),
        modalBody: stagedModalBody,
        modalSuccessActionLabel: t(($) => $.common.delete),
      });
    }
  };

  const disableApp = async (updatedModuleId: string) => {
    // Simply toggle the disabled flag while preserving order and path (folder membership)
    const updatedModules = moduleSettings.map((moduleSettingItem) => {
      if (moduleSettingItem.id === updatedModuleId) {
        return {
          ...moduleSettingItem,
          disabled: true,
        };
      }
      return moduleSettingItem;
    });

    await dispatch(updateModuleSettings(updatedModules));
  };

  const cleanupModuleSettings = async (updatedModuleId: string) => {
    const updatedModules = moduleSettings.filter(
      (moduleItem) => moduleItem.id !== updatedModuleId,
    );
    const nonDeletedExtensionsConfigs = Object.entries(extensionsSettings || {})
      .filter(([extensionId]) => extensionId !== updatedModuleId)
      .reduce(
        (prev, [extensionId, extensionConfig]) => ({
          ...prev,
          [extensionId]: extensionConfig,
        }),
        {},
      );
    await dispatch(
      updateModuleSettings(updatedModules, {
        disableSuccessAlert: !!appId,
      }),
    );
    await dispatch(updateExtensionSettingsAction(nonDeletedExtensionsConfigs));
  };

  const deleteApp = async (updatedModuleId: string) => {
    if (appId) {
      await deleteCustomApp({ id: appId });
    }
    await cleanupModuleSettings(updatedModuleId);
  };

  const handleDeleteModuleConfirm = async () => {
    if (!deletingModuleData) return;
    setIsDeletingModule(true);
    const { moduleType, moduleId } = deletingModuleData;
    // if it is a core module we need to set it as disabled only
    if (moduleType === 'module') {
      await disableApp(moduleId as string);
    } else {
      // for extension modules we need to delete the module
      await deleteApp(moduleId as string);
    }

    // close deleting confirmation modal by resetting the deleting module data state.
    setDeletingModuleData(null);
    setIsDeletingModule(false);
    history.push(APP_SETUP_PAGE.path);
  };

  // We don't want to show the remove app button in add mode
  if (isAddMode) return null;

  return (
    <div>
      {showBuilderDeleteModal && builder && appModule && (
        <DeleteAppBuilderModal
          appId={builder.id}
          appName={builder.name}
          onClose={() => setShowBuilderDeleteModal(false)}
          onDeleted={async () => {
            await cleanupModuleSettings(appModule.id);
            history.push(APP_SETUP_PAGE.path);
          }}
        />
      )}
      {deletingModuleData && (
        <AlertModal
          className={classes.actionModal}
          open={Boolean(deletingModuleData)}
          title={deletingModuleData?.modalTitle}
          successButtonLabel={deletingModuleData?.modalSuccessActionLabel}
          onSuccess={handleDeleteModuleConfirm}
          onClose={() => setDeletingModuleData(null)}
          cancelButtonLabel="Cancel"
          Icon={AlertCircleIcon}
          noCancelButton={false}
          isDeleteModal
          isLoading={isDeletingModule}
        >
          {deletingModuleData?.modalBody}
        </AlertModal>
      )}
      {!appModule?.disabled && (
        <div>
          <BaseTypography fontType="18Medium">
            {t(($) =>
              isCoreApp
                ? $.page_apps.remove_app_modal.disable.title
                : $.page_apps.remove_app_modal.remove.title,
            )}
          </BaseTypography>
          {!isMarketplaceApp && (
            <BaseTypography
              fontType="13Regular"
              className={classes.removeAppContent}
            >
              {t(($) =>
                isCoreApp
                  ? $.page_apps.remove_app_modal.disable.description
                  : $.page_apps.remove_app_modal.remove.description,
              )}
            </BaseTypography>
          )}
          <Button
            onClick={handleDeleteModuleActionClicked}
            variant="contained"
            color="primary"
            className={
              isCoreApp ? classes.disabledButton : classes.deleteButton
            }
          >
            {t(($) => (isCoreApp ? $.common.disable : $.common.delete))}
          </Button>
        </div>
      )}
    </div>
  );
};
